import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Mail, Users, RefreshCw } from 'lucide-react';
import Seo from '../components/Seo';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 20, 2026";

  const sections = [
    {
      id: "collection",
      title: "1. Information We Collect",
      icon: Database,
      content: "We collect information to provide and improve our services. This includes:",
      list: [
        "Account information (name, email, password)",
        "Profile information (license type, experience, resume)",
        "Job postings and application data",
        "Payment information (processed securely by Stripe/PayPal)",
        "Usage data (pages visited, features used)",
        "Device and browser information"
      ]
    },
    {
      id: "use",
      title: "2. How We Use Your Information",
      icon: Eye,
      content: "We use the information we collect to:",
      list: [
        "Provide and maintain our job board services",
        "Connect employers with qualified social workers",
        "Process payments and subscriptions",
        "Send important updates about your account",
        "Improve our platform and user experience",
        "Prevent fraud and ensure platform security"
      ]
    },
    {
      id: "sharing",
      title: "3. Information Sharing",
      icon: Users,
      content: "We respect your privacy and limit information sharing:",
      list: [
        "We do NOT sell or trade your personal data",
        "Job seeker profiles are visible to subscribed employers only",
        "Employers see applicant names; contact details require a membership",
        "We share data with service providers (payment processors, hosting) only as needed",
        "We may disclose information if required by law"
      ]
    },
    {
      id: "protection",
      title: "4. Data Protection",
      icon: Lock,
      content: "We implement security measures to protect your information:",
      list: [
        "Encrypted data transmission (SSL/TLS)",
        "Secure password hashing",
        "Regular security audits",
        "Access controls for sensitive data",
        "Secure payment processing through PCI-compliant providers"
      ]
    },
    {
      id: "rights",
      title: "5. Your Rights",
      icon: Shield,
      content: "You have control over your personal information:",
      list: [
        "Access your data through your account dashboard",
        "Update or correct your profile information",
        "Delete your account and associated data",
        "Opt out of non-essential communications",
        "Request a copy of your data"
      ]
    },
    {
      id: "cookies",
      title: "6. Cookies & Tracking",
      icon: Eye,
      content: "We use cookies and similar technologies to:",
      list: [
        "Keep you logged in to your account",
        "Remember your preferences",
        "Analyze site usage and improve our services",
        "You can control cookies through your browser settings"
      ]
    },
    {
      id: "changes",
      title: "7. Changes to This Policy",
      icon: RefreshCw,
      content: "We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the 'Last Updated' date. Continued use of our platform after changes constitutes acceptance of the updated policy."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        title="Privacy Policy"
        description="How I Need A Social Worker collects, uses and protects your information — account data, profiles, payments and your privacy rights on our social work platform."
        path="/privacy"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            <Lock className="w-4 h-4" />
            Privacy
          </div>
          <h1 className="font-serif text-4xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Last Updated: {lastUpdated}
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 mb-8">
          <p className="text-gray-700 leading-relaxed">
            INeedASocialWorker ("we," "our," or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, and safeguard your information when 
            you use our job board platform for social work professionals.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-serif text-xl font-semibold text-gray-900">
                  {section.title}
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                {section.content}
              </p>
              {section.list && (
                <ul className="space-y-2">
                  {section.list.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-600">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Contact Section */}
          <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <h2 className="font-serif text-xl font-semibold text-gray-900">
                8. Contact Us
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or want to exercise your privacy rights, 
              please contact us:
            </p>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">INeedASocialWorker Support</p>
              <a 
                href="mailto:support@ineedasocialworker.com" 
                className="text-primary hover:underline"
              >
                support@ineedasocialworker.com
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            By using INeedASocialWorker, you agree to this Privacy Policy.
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="text-sm text-primary hover:underline">
              Terms & Policies
            </Link>
            <Link to="/contact" className="text-sm text-primary hover:underline">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
